import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { templateKeys } from './useTemplates';
import type { Template } from '../../lib/types/template.types';

/**
 * Toggle template favorite status mutation
 * Uses optimistic updates for instant UI feedback
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * const toggleFavorite = useToggleFavorite();
 * toggleFavorite.mutate(templateId);
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Template>(`/api/templates/${id}/favorite`);
      return data;
    },
    // Optimistic update for instant UI feedback
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: templateKeys.detail(id) });

      // Snapshot previous value
      const previousTemplate = queryClient.getQueryData<Template>(templateKeys.detail(id));

      // Optimistically update
      if (previousTemplate) {
        queryClient.setQueryData<Template>(templateKeys.detail(id), {
          ...previousTemplate,
          is_favorite: !previousTemplate.is_favorite,
        });
      }

      return { previousTemplate };
    },
    // Rollback on error
    onError: (_err, id, context) => {
      if (context?.previousTemplate) {
        queryClient.setQueryData(
          templateKeys.detail(id),
          context.previousTemplate
        );
      }
    },
    // Refetch on settle (success or error)
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    },
  });
}
